"use strict";
/**
 * Public URL builder for the on-demand media service: every image
 * read goes through `/api/0/media/{key}`, which serves transformed objects from S3.
 * The public signature is unchanged; a new optional `format` maps to `fmt`.
 *
 * - absolute URLs (external or already-migrated media) are returned unchanged,
 * - plain keys get the `${ENVIRONMENT}/` prefix unless `shouldIncludeEnvironmentPart` is false.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.getCdnImageUrl = getCdnImageUrl;
function getTransformationParam(shouldOptimizeQuality, originalTransformation) {
    const transformation = originalTransformation === null || originalTransformation === void 0 ? void 0 : originalTransformation.replace(/^\/+/, '');
    if (!transformation && !shouldOptimizeQuality) {
        return '';
    }
    if (!transformation && shouldOptimizeQuality) {
        return 'q_auto';
    }
    if (transformation && !shouldOptimizeQuality) {
        return transformation;
    }
    return `${transformation},q_auto`;
}
function toStorageKey(file, shouldIncludeEnvironmentPart) {
    // replace ':' -> '/' due to dummy images
    const normalized = file.replace(':', '/').replace(/^\/+/, '');
    const environment = process.env.ENVIRONMENT;
    if (shouldIncludeEnvironmentPart && environment && !normalized.startsWith(`${environment}/`)) {
        return `${environment}/${normalized}`;
    }
    return normalized;
}
function buildMediaUrl(key, transformation, shouldOptimizeQuality, addWatermarkedLogo, format) {
    const publicUrl = (process.env.PUBLIC_URL || '').replace(/\/+$/, '');
    const encodedKey = key.split('/').map(encodeURIComponent).join('/');
    const query = [];
    const transformationParam = getTransformationParam(shouldOptimizeQuality, transformation);
    if (transformationParam) {
        query.push(`tr=${transformationParam}`);
    }
    if (addWatermarkedLogo) {
        query.push('wm=1');
    }
    if (format) {
        query.push(`fmt=${format}`);
    }
    return `${publicUrl}/api/0/media/${encodedKey}${query.length ? `?${query.join('&')}` : ''}`;
}
function getCdnImageUrl({ transformation, file, shouldOptimizeQuality = true, shouldIncludeEnvironmentPart = true, addWatermarkedLogo = false, format, }) {
    var _a;
    if (!(file === null || file === void 0 ? void 0 : file.trim())) {
        // Defensive: callers may pass a missing source (`getAssetImageSource(asset)!`).
        return '';
    }
    // Absolute URLs (external or already-migrated media) are returned unchanged.
    if (/^https?:\/\//i.test(file)) {
        return file;
    }
    // Some call sites compose `web/company/${value}` / `web/account/${value}` while the
    // value may already be an absolute URL — return the embedded URL as-is.
    const embeddedUrl = (_a = /https?:\/\/\S+/i.exec(file)) === null || _a === void 0 ? void 0 : _a[0];
    if (embeddedUrl) {
        return embeddedUrl;
    }
    return buildMediaUrl(toStorageKey(file, shouldIncludeEnvironmentPart), transformation, shouldOptimizeQuality, addWatermarkedLogo, format);
}
